/**
 * StoryNav (web) — the article's internal table of contents, built from the story's `section` blocks
 * (icon + title). Scroll-spies the section headers to highlight the active one, and each item scrolls to
 * its section (the headers carry `scroll-mt` for the offset). The Odessia-style sticky sidebar.
 */
import type { Story } from '@elara/client-core/stories/stories';

import { useEffect, useState } from 'react';

import { cn } from '@elara/client-core/lib/utils';

import { useStoriesState } from '#states/hooks.js';
import { MaterialIcon } from '#ui';

export function StoryNav({ story }: { story: Pick<Story, 'content'> }) {
  const stories = useStoriesState();
  const sections = stories.sections(story);
  const [activeSlug, setActiveSlug] = useState<string | undefined>(sections[0]?.slug);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.find((entry) => entry.isIntersecting);
        if (visible) setActiveSlug(visible.target.id);
      },
      // Activate a section once its header reaches the upper third of the viewport.
      { rootMargin: '-15% 0px -75% 0px' },
    );
    for (const section of sections) {
      const el = document.querySelector(`#${section.slug}`);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
    // `StoriesState` hands back the same story object until the document changes.
  }, [story]); // eslint-disable-line react-hooks/exhaustive-deps

  if (sections.length === 0) return null;

  return (
    <nav className="sticky top-24 flex flex-col gap-1 border-l pl-8">
      {sections.map((section) => (
        <a
          key={section.slug}
          href={`#${section.slug}`}
          onClick={(e) => {
            // Smooth-scroll within the page's scroll container (anchor jumps would be instant and can
            // fight the custom overflow container); the headers carry `scroll-mt` for the offset.
            e.preventDefault();
            setActiveSlug(section.slug);
            document.querySelector(`#${section.slug}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
          className={cn(
            'flex items-center gap-3 rounded px-3 py-2 text-sm transition-colors',
            activeSlug === section.slug
              ? 'bg-background-tertiary font-medium text-foreground'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          <MaterialIcon icon={section.icon} />
          <span className="truncate">{section.title}</span>
        </a>
      ))}
    </nav>
  );
}
